import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { amount, date, money } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/finance.css';

function CreateBankModal({ defaultCurrency, onClose, onCreated }: { defaultCurrency: string; onClose: () => void; onCreated: () => void }) {
  const [bankName, setBankName] = useState('');
  const [holder, setHolder] = useState('');
  const [iban, setIban] = useState('');
  const [swift, setSwift] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (bankName.trim().length < 2 || holder.trim().length < 2) { setErr('Banco y titular son obligatorios.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.post('/bank-accounts', {
        bankName: bankName.trim(),
        holder: holder.trim(),
        iban: iban.trim() || undefined,
        swift: swift.trim() || undefined,
        currency: currency.trim() || defaultCurrency,
        isDefault,
      });
      onCreated();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo crear la cuenta.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div><span className="eyebrow">Nueva</span><h2 className="h2" style={{ marginTop: 4, fontSize: 20 }}>Cuenta bancaria</h2></div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          <div className="field"><label>Banco</label><input className="input" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="p. ej. Emirates NBD" autoFocus /></div>
          <div className="field"><label>Titular</label><input className="input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Account holder legal name" /></div>
          <div className="field"><label>IBAN</label><input className="input" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="AE07 0331 2345 6789 0123 456" /></div>
          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 140 }}><label>SWIFT / BIC</label><input className="input" value={swift} onChange={(e) => setSwift(e.target.value)} /></div>
            <div className="field" style={{ width: 110 }}><label>Moneda</label><input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} /></div>
          </div>
          <label className="row gap-8 center" style={{ cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Marcar como cuenta principal
          </label>
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>{busy ? 'Creando…' : 'Add account'}</button>
        </div>
      </div>
    </div>
  );
}

interface BankAccount {
  id: string;
  bankName: string | null;
  holder: string | null;
  iban: string | null;
  accountNumber: string | null;
  currency: string;
  isDefault: boolean;
}
interface Payment {
  id: string;
  number: string;
  counterpartyName: string | null;
  totalMinor: number;
  currency: string;
  issueDate: string | null;
  status: string;
}

export function Treasury() {
  const { company } = useSession();
  const nav = useNavigate();
  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    if (!company) return;
    setAccounts(null);
    api.get<BankAccount[]>('/bank-accounts').then(setAccounts).catch(() => setAccounts([]));
  };

  useEffect(() => {
    if (!company) return;
    setAccounts(null);
    setPayments(null);
    api.get<BankAccount[]>('/bank-accounts').then(setAccounts).catch(() => setAccounts([]));
    api.get<Payment[]>('/documents?types=payment').then(setPayments).catch(() => setPayments([]));
  }, [company]);

  const cur = company?.currency ?? 'AED';
  const collected = (payments ?? []).reduce((s, p) => s + p.totalMinor, 0);

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Finanzas</span>
          <h1 className="h1">Treasury</h1>
          <p className="page-head__sub">Tus cuentas bancarias y el movimiento de caja. Cada cobro registrado queda trazado con la factura que salda.</p>
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi kpi--good">
          <div className="kpi__label">Cobrado (acumulado)</div>
          <div className="kpi__value">{money(collected, cur)}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Movimientos</div>
          <div className="kpi__value">{payments?.length ?? '—'}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Cuentas activas</div>
          <div className="kpi__value">{accounts?.length ?? '—'}</div>
        </div>
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card__head"><span className="card__title">Movimientos de caja</span></div>
          <div className="card__body card__body--flush">
            {!payments && <div className="empty">Loading…</div>}
            {payments && payments.length === 0 && <div className="empty">Sin movimientos registrados.</div>}
            {payments && payments.map((p) => (
              <button key={p.id} className="todo-item" onClick={() => nav(`/documentos/${p.id}`)}>
                <Icon name="wallet" size={17} className="todo-item__ico" style={{ color: 'var(--accent)' }} />
                <span className="todo-item__body">
                  <span className="todo-item__title">{p.number}{p.counterpartyName ? ` · ${p.counterpartyName}` : ''}</span>
                  <span className="todo-item__meta">{date(p.issueDate)}</span>
                </span>
                <span className="todo-item__amt" style={{ color: 'var(--accent)' }}>+{amount(p.totalMinor, p.currency)} {p.currency}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="col">
          <div className="row between center" style={{ marginBottom: 12 }}>
            <span className="eyebrow">Cuentas bancarias</span>
            <button className="btn btn--ghost btn--sm" onClick={() => setCreating(true)}><Icon name="plus" size={14} />Add</button>
          </div>
          {!accounts && <div className="faint">Loading…</div>}
          {accounts && accounts.length === 0 && (
            <div className="card"><div className="empty">No hay cuentas bancarias registradas.</div></div>
          )}
          {accounts && accounts.map((a) => (
            <div key={a.id} className="bank-card">
              {a.isDefault && <span className="bank-card__default">Principal</span>}
              <div className="bank-card__top">
                <div>
                  <div className="bank-card__name">{a.bankName ?? 'Cuenta'}</div>
                  <div className="bank-card__holder">{a.holder ?? '—'}</div>
                </div>
                <span className="bank-card__cur">{a.currency}</span>
              </div>
              <div className="bank-card__iban">{a.iban ?? a.accountNumber ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {creating && <CreateBankModal defaultCurrency={cur} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </main>
  );
}
